import { useState, FC, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import orderBy from 'lodash/orderBy';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RootState } from 'state/store';
import { useAppThunkDispatch } from 'state/store';
import {
  fetchTaskTypes,
  selectCanFetchTaskTypes,
  selectTaskTypes,
} from 'state/TasksList/allTasks/allTasksSlice';
import {
  selectFiltersSettingsTypeId,
  createFilterSetting,
  updateFilterSetting,
} from 'state/Settings/Filters/FiltersSlice';
import { TaskListFilters } from 'types/store/tasksList';
import { CentralizedTasksTableTab } from 'utils/types/centralizedTasks';
import FilterButton from 'Organisms/TableHeaderActionButtons/FilterButton';
import MoreActionsPopup, {
  OptionProps,
} from 'Organisms/TableHeaderActionButtons/MoreActionsPopup';
import TasksFiltersSidePanel from '../../TasksFiltersSidePanel/TasksFiltersSidePanel';
import AppliedFiltersTags from '../../AppliedFiltersTags/AppliedFiltersTags';
import SearchFilter from './SearchFilter';
import ExportIcon from 'assets/icons/export.svg';

interface ActionsHeaderProps {
  tableName: CentralizedTasksTableTab;
  filters: TaskListFilters;
  onUpdateFilters: (filters: TaskListFilters) => void;
  onSearchFilterChange: (value: string) => void;
  onExport: () => void;
}

const ActionsHeader: FC<ActionsHeaderProps> = ({
  tableName,
  filters,
  onUpdateFilters,
  onSearchFilterChange,
  onExport,
}) => {
  const dispatch = useDispatch();
  const thunkDispatch = useAppThunkDispatch();
  const { newTaskTypes = false } = useFlags();
  const [shouldDisplayFilterComponent, setShouldDisplayFilterComponent] =
    useState<boolean>(false);

  const canFetchTaskTypes = useSelector(selectCanFetchTaskTypes);
  const taskTypes = useSelector(selectTaskTypes);

  useEffect(() => {
    if (canFetchTaskTypes) {
      thunkDispatch(fetchTaskTypes(newTaskTypes));
    }
  }, [canFetchTaskTypes, thunkDispatch, newTaskTypes]);

  const typeFilterOptions = useMemo(
    () =>
      orderBy(
        Object.entries(taskTypes).map(([code, name]) => ({
          value: code,
          label: name,
        })),
        'label'
      ),
    [taskTypes]
  );

  const filtersSettingsTypeId = useSelector((state: RootState) =>
    selectFiltersSettingsTypeId(state, tableName)
  );

  const handleSetFiltersSettings = useCallback(
    (filtersSettings: TaskListFilters) => {
      const { search, ...otherFilters } = filtersSettings;
      if (filtersSettingsTypeId) {
        dispatch(
          updateFilterSetting({
            id: filtersSettingsTypeId,
            updateFields: { filter_settings: otherFilters },
          })
        );
      } else {
        dispatch(
          createFilterSetting({
            filter_type: tableName,
            filter_settings: otherFilters,
          })
        );
      }
      onUpdateFilters(otherFilters);
    },
    [filtersSettingsTypeId, onUpdateFilters, tableName, dispatch]
  );

  const toggleFilterComponent = () =>
    setShouldDisplayFilterComponent((prev) => !prev);

  const moreActionsList: OptionProps[] = useMemo(
    () => [
      {
        label: intl.get('TASKS_LIST_PAGE.EXPORT_TASKS'),
        selectOption: onExport,
        iconSrc: ExportIcon,
        tabName: tableName,
        dataCy: 'table-header__export-button',
      },
    ],
    [tableName, onExport]
  );

  return (
    <>
      <TasksFiltersSidePanel
        open={shouldDisplayFilterComponent}
        onClose={toggleFilterComponent}
        filters={filters}
        onUpdateFilters={handleSetFiltersSettings}
        hideAssigneeFilter={tableName === 'myCentralizedTasksTable'}
        typeFilterOptions={typeFilterOptions}
      />
      <SearchFilter
        initialSearchFilterValue={filters.search || ''}
        onSearchFilterChange={onSearchFilterChange}
        tableName={tableName}
      >
        <FilterButton
          aria-label={`${tableName}__filter-button`}
          onToggleFilter={toggleFilterComponent}
          aria-pressed={shouldDisplayFilterComponent}
        />
        <MoreActionsPopup options={moreActionsList} />
      </SearchFilter>
      <AppliedFiltersTags
        filters={filters}
        onUpdateFilters={handleSetFiltersSettings}
        tableName={tableName}
        taskTypeNames={taskTypes}
      />
    </>
  );
};

export default ActionsHeader;
